import { memo, useCallback } from "react";
import { generatePath, Link, useNavigate } from "react-router";

import type { HpbPage } from "@/lib/gql/__generated__/graphql";

import { NEW_ITEM_DUPLICATED_ITEM_ID_PARAMS, PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import EditDocument from "@/components/ui/icon/edit-document";
import ThreeDotsHorizontal from "@/components/ui/icon/three-dots-horizontal";
import { Image } from "@/components/ui/image/image";

type Props = {
	hotelID: string;

	HPBPage: HpbPage;

	onDelete: ({ uuid }: { uuid: string }) => Promise<void>;
};

export const HPBListItem = memo(({ hotelID, HPBPage, onDelete }: Props) => {
	const { thumbnail, title, uuid } = HPBPage;

	const editPagePath = generatePath(PATHS.HOTEL.HPB.EDIT.UUID, { hotelID, uuid });
	const duplicatePagePath = `${generatePath(PATHS.HOTEL.HPB.EDIT.NEW, { hotelID })}?${NEW_ITEM_DUPLICATED_ITEM_ID_PARAMS}=${uuid}`;

	const navigate = useNavigate();

	const handleClickCopyButton = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();

			navigate(duplicatePagePath);
		},
		[duplicatePagePath, navigate],
	);

	const handleClickDeleteButton = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			await onDelete({ uuid });
		},
		[onDelete, uuid],
	);

	const handleClick = useCallback(() => {
		navigate(editPagePath);
	}, [editPagePath, navigate]);

	return (
		<div
			onClick={handleClick}
			className="group flex items-center justify-between gap-x-6 py-3 hover:cursor-pointer"
		>
			<div className="flex items-center gap-x-6">
				<Image src={thumbnail} className="h-16 w-16 rounded object-cover" />
				<p className="group-hover:underline">{title}</p>
			</div>
			<div className="flex items-center gap-x-6">
				<Link to={editPagePath} onClick={(e) => e.stopPropagation()} viewTransition>
					<Button type="button" variant="input-ish" size="icon" tooltip="編集">
						<EditDocument size="sm" />
					</Button>
				</Link>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant={"input-ish"}
							size={"icon"}
							onClick={(e) => e.stopPropagation()}
						>
							<ThreeDotsHorizontal size="sm" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="w-40">
						<div className="grid space-y-1 text-left">
							<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickCopyButton}>
								複製する
							</Button>

							<Button variant={"no-outline"} textAlign={"left"} onClick={handleClickDeleteButton}>
								<p className="font-medium text-error-600">削除する</p>
							</Button>
						</div>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
});

HPBListItem.displayName = "HPBListItem";
